import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-arrows-spin',
    title: '1. SAP S/4HANA Migration & Upgrade',
    desc: 'Seamlessly transition legacy SAP ECC environments to SAP S/4HANA using Greenfield, Brownfield, or Selective Data Transition strategies with minimal downtime.',
  },
  {
    icon: 'fa-solid fa-cloud-gear',
    title: '2. RISE with SAP & Cloud Transformations',
    desc: 'Modernize your core enterprise setup by migrating on-premises SAP landscapes to hyperscaler clouds (AWS, Azure, GCP) via RISE with SAP.',
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: '3. SAP Business Technology Platform (BTP)',
    desc: 'Build, extend, and integrate custom business applications using SAP BTP, enabling clean core architecture without altering standard SAP code.',
  },
  {
    icon: 'fa-solid fa-coins',
    title: '4. Financial Management (SAP FI/CO)',
    desc: 'Streamline general ledger, accounts payable/receivable, asset accounting, and real-time financial reporting to ensure global compliance.',
  },
  {
    icon: 'fa-solid fa-boxes-stacked',
    title: '5. Supply Chain & Logistics (SAP MM/SD/WM)',
    desc: 'Optimize procurement, inventory movement, sales order management, and warehouse logistics through integrated SAP supply chain modules.',
  },
  {
    icon: 'fa-solid fa-people-roof',
    title: '6. SAP SuccessFactors HR Transformation',
    desc: 'Automate human capital management (HCM), global payroll, talent acquisition, and employee performance workflows using SAP SuccessFactors.',
  },
  {
    icon: 'fa-solid fa-gears',
    title: '7. SAP Plant Maintenance & Quality (PM/QM)',
    desc: 'Maximize equipment efficiency, automate maintenance schedules, and enforce strict quality assurance across manufacturing plants.',
  },
  {
    icon: 'fa-solid fa-chart-line-up',
    title: '8. SAP Analytics Cloud (SAC) & BW/4HANA',
    desc: 'Unify enterprise data to build real-time predictive dashboards, financial planning models, and executive reporting views.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '9. SAP Security, GRC & Authorization Audit',
    desc: 'Implement robust Governance, Risk, and Compliance (GRC) frameworks, role-based authorizations, and automated security monitoring.',
  },
  {
    icon: 'fa-solid fa-headset',
    title: '10. SAP Application Management Services (AMS)',
    desc: 'Ensure continuous operational health with 24/7 L1-L3 SAP technical support, functional enhancement, performance tuning, and patch updates.',
  },
];

const whyChooseUs = [
  'Certified SAP S/4HANA migration architects and functional experts',
  'Proven methodology for "Clean Core" extension using SAP BTP',
  'Accelerated migration toolkits reducing project execution timelines',
  'Multi-hyperscaler cloud deployment expertise (AWS, Azure, GCP)',
  'Strict adherence to global SAP compliance and security standards',
  'Seamless integration with third-party CRM, CPQ, and WMS platforms',
  'Real-time financial and operational visibility via SAC dashboards',
  'End-to-end organizational change management and user training',
  'Flexible 24/7 Application Management Support (AMS) models',
  'High return on investment through optimized operational licensing',
];

const processSteps = [
  { step: '01', title: 'SAP Assessment & Roadmap', desc: 'Audit legacy SAP infrastructure, code base, customization levels, and define target S/4HANA architecture.' },
  { step: '02', title: 'Data Cleansing & Strategy', desc: 'Archive historical records, clean master data, and map data transfer flows for high migration fidelity.' },
  { step: '03', title: 'System Blueprint & Build', desc: 'Configure core modules, build extensions on SAP BTP, and prepare integration interfaces.' },
  { step: '04', title: 'Testing & Validation', desc: 'Perform user acceptance testing (UAT), stress tests, integration checks, and security audits.' },
  { step: '05', title: 'Cutover & Go-Live Execution', desc: 'Execute controlled cutover sequences with minimum system downtime and instant operational readiness.' },
  { step: '06', title: 'Post-Go-Live & AMS Support', desc: 'Provide continuous hypercare, user training, and ongoing SAP Application Management Services.' },
];

const techStack = [
  { category: 'Core ERP Platforms', items: ['SAP S/4HANA', 'SAP ECC 6.0', 'RISE with SAP', 'SAP S/4HANA Cloud'] },
  { category: 'Integration & Extension', items: ['SAP BTP', 'SAP Integration Suite', 'SAP Extension Suite', 'SAP Fiori'] },
  { category: 'Analytics & HR', items: ['SAP Analytics Cloud (SAC)', 'SAP BW/4HANA', 'SAP SuccessFactors', 'SAP Business One'] },
  { category: 'Database & Cloud', items: ['SAP HANA DB', 'AWS', 'Microsoft Azure', 'Google Cloud Platform'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const SAPEnterpriseSystems = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Technology Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              SAP Enterprise <span className="gradient-text-cyan">Systems</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we empower enterprises to transform into intelligent digital businesses. From S/4HANA migrations to SAP BTP extensions, we optimize your core operational engine for speed, agility, and scale.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Modernize Your SAP Landscape &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              SAP Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">SAP Technology Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">SAP Transformation Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Maximize Your SAP Enterprise Potential with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform legacy ERP bottlenecks into high-speed digital engines backed by S/4HANA and cloud-native SAP architectures.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with an SAP Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SAPEnterpriseSystems;